import React, { useEffect, useRef, useState } from 'react';
import { Row, Col, Button, Card, Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { MdFlight, MdAttachFile, MdCloudUpload } from 'react-icons/md';
import { setSelectedArchive } from '_redux/slices/archive/archive';
import { selectFirebaseInfo } from '_redux/slices/main-screen/user';
import {
    subscribeToArchive,
    deleteArchive,
    uploadAttachment,
    appendAttachments,
} from '_services/archive-service';
import { Colors } from '_styles';

const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString();
};

const formatDateTime = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString();
};

const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const CARD_STYLE = {
    marginBottom: 16,
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
    backgroundColor: 'white',
};

const CONTENT_STYLE = { padding: 16 };

const ROW_STYLE = {
    display: 'flex',
    gap: 24,
    flexWrap: 'wrap',
    alignItems: 'flex-start',
};

const LABEL_STYLE = {
    fontSize: 12,
    fontWeight: 600,
    color: '#6b7280',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
};

const VALUE_STYLE = {
    fontSize: 14,
    color: '#374151',
    margin: 0,
    fontWeight: 500,
};

const tableContainerStyle = {
    overflowX: 'auto',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
};

const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: 'white',
    fontSize: 14,
};

const thStyle = {
    backgroundColor: '#f8fafc',
    color: '#374151',
    fontWeight: 600,
    padding: '10px 14px',
    textAlign: 'left',
    borderBottom: '2px solid #e5e7eb',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
};

const tdStyle = {
    padding: '10px 14px',
    borderBottom: '1px solid #f3f4f6',
    color: '#374151',
    verticalAlign: 'middle',
};

const InfoItem = ({ label, children }) => (
    <div style={{ flex: 1, minWidth: 140 }}>
        <div style={LABEL_STYLE}>{label}</div>
        <div style={VALUE_STYLE}>{children || '—'}</div>
    </div>
);

const CardHeader = ({ color, icon: Icon, title }) => (
    <Card.Header style={{
        backgroundColor: color,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 16px',
    }}>
        {Icon && <Icon size={18} color="white" />}
        <p style={{ margin: 0, color: 'white', fontSize: 15, fontWeight: 600 }}>{title}</p>
    </Card.Header>
);

const buildCreatorName = (dbUser) => {
    const first = dbUser?.firstName?.trim?.();
    const last = dbUser?.lastName?.trim?.();
    if (first || last) return [first, last].filter(Boolean).join(' ');
    return dbUser?.name?.trim?.() || dbUser?.email || 'Unknown user';
};
const buildCreatorId = (dbUser) => dbUser?.firebaseId ?? dbUser?.uid ?? null;

const ArchiveDetail = ({ archiveId, onBack }) => {
    const dispatch = useDispatch();
    const dbUser = useSelector(selectFirebaseInfo);
    const [archive, setArchive] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [newFiles, setNewFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (!archiveId) return;
        const unsubscribe = subscribeToArchive(archiveId, setArchive);
        return () => unsubscribe();
    }, [archiveId]);

    const handleBack = () => {
        dispatch(setSelectedArchive(null));
        onBack?.();
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await deleteArchive(archiveId);
            handleBack();
        } finally {
            setDeleting(false);
            setConfirmDelete(false);
        }
    };

    const handleUpload = async () => {
        if (newFiles.length === 0) return;
        setUploading(true);
        setUploadError(null);
        try {
            const uploader = { id: buildCreatorId(dbUser), name: buildCreatorName(dbUser) };
            const uploaded = [];
            for (const file of newFiles) {
                try {
                    const meta = await uploadAttachment(archiveId, file, uploader);
                    uploaded.push(meta);
                } catch (err) {
                    console.error('Failed to upload attachment', file.name, err);
                }
            }
            if (uploaded.length > 0) {
                await appendAttachments(archiveId, uploaded, archive.attachments ?? []);
            }
            setNewFiles([]);
            if (fileInputRef.current) fileInputRef.current.value = '';
        } catch (err) {
            setUploadError(err?.message ?? 'Upload failed.');
        } finally {
            setUploading(false);
        }
    };

    if (!archive) {
        return (
            <div style={{ padding: 32, textAlign: 'center', color: '#6b7280', fontSize: 14 }}>
                Loading archive...
            </div>
        );
    }

    const attachments = archive.attachments ?? [];

    return (
        <>
            <Row className="mt-3 mb-3 align-items-center">
                <Col>
                    <Button variant="outline-secondary" size="sm" onClick={handleBack}>
                        ← Back to list
                    </Button>
                </Col>
                <Col xs="auto" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {!confirmDelete ? (
                        <Button variant="outline-danger" size="sm" onClick={() => setConfirmDelete(true)}>
                            Delete
                        </Button>
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 13, color: '#dc2626', fontWeight: 500 }}>
                                Are you sure?
                            </span>
                            <Button variant="danger" size="sm" onClick={handleDelete} disabled={deleting}>
                                {deleting ? 'Deleting…' : 'Yes, delete'}
                            </Button>
                            <Button variant="outline-secondary" size="sm" onClick={() => setConfirmDelete(false)}>
                                Cancel
                            </Button>
                        </div>
                    )}
                </Col>
            </Row>

            <Card style={CARD_STYLE}>
                <CardHeader color={Colors.PRM_LIGHT_BLUE} icon={MdFlight} title={`Flight ${archive.flightNumber}`} />
                <Card.Body style={CONTENT_STYLE}>
                    <div style={ROW_STYLE}>
                        <InfoItem label="Flight Number">{archive.flightNumber}</InfoItem>
                        <InfoItem label="Origin">{archive.origin}</InfoItem>
                        <InfoItem label="Destination">{archive.destination}</InfoItem>
                        <InfoItem label="Flight Date">{formatDate(archive.flightDate)}</InfoItem>
                    </div>
                    <div style={{ ...ROW_STYLE, marginTop: 16 }}>
                        <InfoItem label="Created By">{archive.createdBy?.name}</InfoItem>
                        <InfoItem label="Created At">{formatDateTime(archive.createdAt)}</InfoItem>
                        <InfoItem label="Last Updated">{formatDateTime(archive.updatedAt)}</InfoItem>
                        <InfoItem label="Airport">{archive.iata}</InfoItem>
                    </div>
                </Card.Body>
            </Card>

            <Card style={CARD_STYLE}>
                <CardHeader color="#6b7280" icon={MdAttachFile} title={`Documents (${attachments.length})`} />
                <Card.Body style={{ padding: 0 }}>
                    {attachments.length === 0 ? (
                        <div style={{ padding: '24px 16px', textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
                            No documents have been attached yet. Use the uploader below to add some.
                        </div>
                    ) : (
                        <div style={tableContainerStyle}>
                            <table style={tableStyle}>
                                <thead>
                                    <tr>
                                        <th style={thStyle}>Filename</th>
                                        <th style={thStyle}>Size</th>
                                        <th style={thStyle}>Uploaded By</th>
                                        <th style={thStyle}>Source</th>
                                        <th style={thStyle}>Uploaded At</th>
                                        <th style={thStyle}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {attachments.map((att) => (
                                        <tr key={att.id}>
                                            <td style={{ ...tdStyle, fontWeight: 500 }}>{att.filename}</td>
                                            <td style={tdStyle}>{formatBytes(att.size)}</td>
                                            <td style={tdStyle}>{att.uploadedBy?.name ?? '—'}</td>
                                            <td style={tdStyle}>
                                                <span style={{
                                                    background: att.uploadedBy?.source === 'app' ? '#E0E7FF' : '#D1FAE5',
                                                    color: att.uploadedBy?.source === 'app' ? '#3730A3' : '#065F46',
                                                    padding: '2px 8px',
                                                    borderRadius: 4,
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    textTransform: 'uppercase',
                                                }}>
                                                    {att.uploadedBy?.source ?? 'portal'}
                                                </span>
                                            </td>
                                            <td style={{ ...tdStyle, color: '#6b7280' }}>{formatDateTime(att.uploadedAt)}</td>
                                            <td style={tdStyle}>
                                                <a
                                                    href={att.downloadUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ color: Colors.PRM_LIGHT_BLUE, fontWeight: 500, textDecoration: 'none' }}
                                                >
                                                    View / Download
                                                </a>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <Card style={CARD_STYLE}>
                <CardHeader color="#6b7280" icon={MdCloudUpload} title="Add Documents" />
                <Card.Body style={CONTENT_STYLE}>
                    <Form.Control
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={(e) => setNewFiles(Array.from(e.target.files))}
                        style={{ borderRadius: 8, fontSize: 14 }}
                        disabled={uploading}
                    />
                    {newFiles.length > 0 && (
                        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {newFiles.map((f) => (
                                <span key={f.name} style={{
                                    fontSize: 12,
                                    background: '#e5e7eb',
                                    color: '#374151',
                                    padding: '3px 10px',
                                    borderRadius: 4,
                                }}>
                                    {f.name}
                                </span>
                            ))}
                        </div>
                    )}

                    <div style={{ marginTop: 12 }}>
                        <Button
                            variant="primary"
                            onClick={handleUpload}
                            disabled={uploading || newFiles.length === 0}
                        >
                            {uploading ? 'Uploading…' : `Upload ${newFiles.length || ''} file${newFiles.length === 1 ? '' : 's'}`.trim()}
                        </Button>
                    </div>

                    {uploadError && (
                        <p style={{ color: Colors.DARK_RED, marginTop: 10, fontSize: 13 }}>{uploadError}</p>
                    )}
                </Card.Body>
            </Card>
        </>
    );
};

export default ArchiveDetail;
